import { useMemo, useState } from "react";

import { translate } from "app/filters/i18nFilter";

import type { ISimpleWarningModalHook } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/useSimpleWarningModal";
import { useSimpleWarningModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/useSimpleWarningModal";

export const TRUNCATE_ELLIPSIS = "...";
export const MAX_LISTED_TOPICS = 5;
export const MAX_TOPIC_LENGTH = 20;

interface IEmptyTopicsModalHook extends Pick<ISimpleWarningModalHook, "modal"> {
    openModal: (topics: string[]) => void;
}

export const useEmptyTopicsModal = (onContinue: () => void): IEmptyTopicsModalHook => {
    const [emptyTopics, setEmptyTopics] = useState<string[]>([]);

    const truncateTopicName = (topic: string): string => {
        if (topic.length <= MAX_TOPIC_LENGTH) {
            return topic;
        }

        const prefix = topic.slice(0, Math.ceil(MAX_TOPIC_LENGTH / 2));
        const suffix = topic.slice(topic.length - Math.floor(MAX_TOPIC_LENGTH / 2));
        return prefix.trim() + TRUNCATE_ELLIPSIS + suffix.trim();
    };

    const bodyText = useMemo<string>(() => {
        const topicsCount = emptyTopics?.length ?? 0;

        switch (true) {
            case topicsCount === 1: {
                return translate("llm.prompts.edit.save.topics-warning.text.single", {
                    topic: truncateTopicName(emptyTopics[0]),
                });
            }
            case topicsCount <= MAX_LISTED_TOPICS: {
                const list = emptyTopics
                    .reduce((acc, topic) => [...acc, truncateTopicName(topic)], [] as string[])
                    .map((topic) => `"${topic}"`)
                    .join(", ");

                return translate("llm.prompts.edit.save.topics-warning.text.few", {
                    topics: list,
                });
            }
            case topicsCount > MAX_LISTED_TOPICS: {
                return translate("llm.prompts.edit.save.topics-warning.text.many", {
                    count: topicsCount,
                });
            }
            default: {
                return "";
            }
        }
    }, [emptyTopics]);

    const { openModal: openGenericModal, modal } = useSimpleWarningModal({
        title: translate("llm.prompts.edit.save.topics-warning.title"),
        tooltip: translate("llm.prompts.edit.save.topics-warning.tooltip"),
        text: bodyText,
        ctaText: translate("common.finish"),
        resetState: () => setEmptyTopics([]),
        onSubmit: onContinue,
    });

    const openModal = (topics: string[]): void => {
        setEmptyTopics(topics);
        openGenericModal();
    };

    return {
        openModal,
        modal,
    };
};
